import classnames from "classnames";
import React, { useState } from "react";
import GenericModal from "web/react/components/generic-modal/generic-modal";
import GenericStickyModal from "web/react/components/generic-sticky-modal/generic-sticky-modal";
import PaywallMarkup from "web/react/components/paywall-markup/paywall-markup";
import SendToApp from "web/react/components/send-to-app/send-to-app";
import SendToAppWithTrackedLink from "web/react/components/send-to-app-with-tracked-link/send-to-app-with-tracked-link";
import { useSmallBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import browser from "web/script/utils/browser";
import environment from "web/script/modules/environment";
import styles from "./send-to-app-modal.css";

interface TrackedLinkProps {
    leadUrl: string;
    productId: string;
    reason: string;
}

interface SendToAppModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    url: string;
    analyticsEventLabel: string;
    paywalledClassName?: string | null;
    productImageURL?: string | null;
    appDeepLinkURL?: URL;
    trackedLink?: TrackedLinkProps;
    financialIncentiveWebviewToken?: string;
    downloadCTA?: string;
    extraContent?: () => JSX.Element;
    onCtaClick?: React.EventHandler<React.MouseEvent>;
}

function SendToAppModal({
    isOpen,
    onClose,
    title,
    url,
    analyticsEventLabel,
    paywalledClassName,
    productImageURL,
    appDeepLinkURL,
    trackedLink,
    financialIncentiveWebviewToken,
    downloadCTA,
    extraContent,
    onCtaClick,
}: SendToAppModalProps): JSX.Element {
    let smallScreen = !useSmallBreakpoint();
    let bottomModal = environment.getFeature("mg_ios_modal_bottom") && smallScreen;
    let [closed, setClosed] = useState(true);
    let animateModal = environment.getFeature("mg_animate_web_to_app_modal") && browser.ios;
    // "Download the Lyst App for free"
    downloadCTA = downloadCTA ? downloadCTA : gettext("feed.show_more.app_overlay.cta");
    function handleOpen(): void {
        setClosed(false);
    }
    function onModalClose(): void {
        setClosed(true);
        analytics.event("app_overlay", "modal_dismissed", analyticsEventLabel);
        onClose();
    }

    /**
     * Returns the content for the send2app modal
     * @param {string} downloadCTA CTA string for the branchlink
     * @param {Boolean} animatedModal whether or not this send2App is in the animated modal. As some styling is changed
     */
    function sendToAppChildren(downloadCTA, animatedModal): JSX.Element {
        return !trackedLink ? (
            <SendToApp
                analyticsEventLabel={analyticsEventLabel}
                title={title}
                url={url}
                productImageURL={productImageURL}
                appDeepLinkURL={appDeepLinkURL}
                downloadCTA={downloadCTA}
                extraContent={extraContent}
                animatedModal={animatedModal}
                onCtaClick={onCtaClick}
            />
        ) : (
            <SendToAppWithTrackedLink
                analyticsEventLabel={analyticsEventLabel}
                title={title}
                url={url}
                productImageURL={productImageURL}
                downloadCTA={downloadCTA}
                className={styles.withTrackedLinkContainer}
                appDeepLinkURL={appDeepLinkURL}
                financialIncentiveWebviewToken={financialIncentiveWebviewToken}
                {...trackedLink}
            />
        );
    }
    if (animateModal) {
        return (
            <>
                <GenericStickyModal isOpen={isOpen} onAfterOpen={handleOpen} onClose={onModalClose}>
                    <div className={styles.contentWrapper}>
                        {sendToAppChildren(downloadCTA, true)}
                    </div>
                </GenericStickyModal>
                {paywalledClassName && <PaywallMarkup cssSelector={paywalledClassName} />}
            </>
        );
    }

    return (
        <>
            <GenericModal
                isOpen={isOpen}
                onAfterOpen={handleOpen}
                onClose={onModalClose}
                className={classnames({
                    [styles.modal]: bottomModal,
                    [styles.closed]: closed && bottomModal,
                })}
            >
                {sendToAppChildren(downloadCTA, false)}
            </GenericModal>
            {paywalledClassName && <PaywallMarkup cssSelector={paywalledClassName} />}
        </>
    );
}

export default SendToAppModal;
